import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Trash2, Edit2, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { toast } from 'sonner';
import { StarRating, ScoreBar, KPI_CATEGORIES } from './KPIHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewCard = ({ review, onEdit, onRefresh, onExport, isViewer = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-500';
      case 'submitted': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };
  
  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete this KPI review for ${review.employee_name}?`)) {
      return;
    }
    
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/kpi/reviews/${review.id}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Review deleted');
        onRefresh();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete review');
    } finally {
      setDeleting(false);
    }
  };
  
  const categoryScores = KPI_CATEGORIES.map(cat => {
    const catScores = review.scores?.filter(s => s.category === cat) || [];
    const avg = catScores.length > 0 
      ? catScores.reduce((sum, s) => sum + s.score_percentage, 0) / catScores.length 
      : 0;
    return { category: cat, score: Math.round(avg) };
  });
  
  return (
    <Card className="overflow-hidden">
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
              <span className="text-indigo-600 font-bold">
                {review.employee_name?.split(' ').map(n => n[0]).join('') || '?'}
              </span>
            </div>
            <div>
              <p className="font-semibold">{review.employee_name}</p>
              <p className="text-sm text-gray-500">
                {review.employee_code} | {review.review_period} | {review.period_start} - {review.period_end}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-2xl font-bold text-indigo-600">{Math.round(review.overall_score || 0)}%</p>
              <StarRating rating={review.star_rating || 0} />
            </div>
            <Badge className={getStatusColor(review.status)}>{review.status}</Badge>
            {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </div>
        </div>
      </div>
      
      {expanded && (
        <div className="border-t p-4 bg-gray-50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {categoryScores.map(({ category, score }) => (
              <ScoreBar key={category} label={category} score={score} />
            ))}
          </div>
          
          {review.overall_comment && (
            <div className="mb-4 p-3 bg-white rounded-lg border">
              <p className="text-sm text-gray-500 mb-1">Overall Comment:</p>
              <p className="text-gray-700">{review.overall_comment}</p>
            </div>
          )}
          
          <div className="flex gap-2 justify-end">
            <Button 
              variant="outline" 
              size="sm"
              onClick={(e) => { e.stopPropagation(); onExport(review.id, 'pdf'); }}
              className="text-indigo-600 hover:text-indigo-700"
            >
              <Download className="h-4 w-4 mr-1" />
              PDF
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={(e) => { e.stopPropagation(); onExport(review.id, 'excel'); }}
              className="text-green-600 hover:text-green-700"
            >
              <Download className="h-4 w-4 mr-1" />
              Excel
            </Button>
            {!isViewer && (
              <>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleDelete}
                  disabled={deleting}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`delete-review-${review.id}`}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
                <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onEdit(); }}>
                  <Edit2 className="h-4 w-4 mr-1" />
                  Edit Review
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

export default ReviewCard;
